import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "src/grading/components";
import { useAppDispatch } from "src/store/hooks";
import { useUpdatePasswordMutation } from "src/store/reducers/auth/authApiSlice";
import { handleApiError, handleApiResponse } from "src/utils/api";
import * as Yup from "yup";

interface PasswordChangeModalProps {
  onClose: () => void;
  open: boolean;
  title: string;
  message: string;
  confirmationButtonTitle: string;
}

const PasswordChangeModal = ({
  onClose,
  open,
  title,
  message,
  confirmationButtonTitle,
}: PasswordChangeModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [updatePassword, { isLoading: isUpdatingPassword }] =
    useUpdatePasswordMutation();

  const handleChangePassword = async (values: {
    previousPassword: string;
    proposedPassword: string;
  }) => {
    try {
      const response = await updatePassword({ ...values }).unwrap();
      onClose();
      handleApiResponse(dispatch, response);
    } catch (error) {
      const typedError = error;
      handleApiError(dispatch, typedError);
    }
  };

  const formik = useFormik({
    initialValues: {
      previousPassword: "",
      proposedPassword: "",
    },
    validationSchema: Yup.object({
      previousPassword: Yup.string().required(
        t("profile.required", { defaultValue: "Required" }),
      ),
      proposedPassword: Yup.string().required(
        t("profile.required", { defaultValue: "Required" }),
      ),
    }),
    onSubmit: (values) => handleChangePassword(values),
  });

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="password-change-title"
      aria-describedby="password-change-message"
      PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: (e: FormEvent<HTMLElement>) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        },
      }}
    >
      <DialogTitle id="password-change-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="password-change-message" sx={{ mb: 2 }}>
          {message}
        </DialogContentText>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            type="password"
            label={t("profile.currentPassword", {
              defaultValue: "Current password",
            })}
            fullWidth
            autoComplete="current-password"
            value={values.previousPassword}
            onChange={handleChange("previousPassword")}
            error={touched.previousPassword && Boolean(errors.previousPassword)}
            helperText={touched.previousPassword && errors.previousPassword}
          />
          <TextField
            type="password"
            label={t("auth.newPassword")}
            fullWidth
            autoComplete="new-password"
            value={values.proposedPassword}
            onChange={handleChange("proposedPassword")}
            error={touched.proposedPassword && Boolean(errors.proposedPassword)}
            helperText={touched.proposedPassword && errors.proposedPassword}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          label={confirmationButtonTitle}
          type="submit"
          loading={isUpdatingPassword}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PasswordChangeModal;
